import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { OutreachShell } from '../components/OutreachShell';
import { useBlocks } from '../hooks/useBlocks';
import { useCampaign } from '../hooks/useCampaign';
import { useInvalidateOutreach } from '../hooks/useInvalidateOutreach';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { useReviewQueue } from '../hooks/useReviewQueue';
import { outreachMessages } from '../outreachMessages';
import { ReviewQueueList } from './ReviewQueueList';
import { ReviewPitchCard } from './ReviewPitchCard';
import { ReviewStepBar } from './ReviewStepBar';
import { ReviewAllDone } from './ReviewAllDone';
import { currentReviewRow, isOpenPitch, nextOpenAfter, visibleReviewRows } from './reviewState';
import { hintLines } from './reviewHints';
import { useReviewKeys } from './useReviewKeys';
import { reviewMessages as messages } from './reviewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from './Review.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Today's pitches (design outreach-v2 h1, h2; route `/outreach/campaigns/:uuid/review`, the push
 * `outreachPitchesReady` opens it at 8:00). One pitch at a time: approve, edit or skip, with the
 * keys A, E and S. "Approve all" is `review.approveRest` (after 10 small-edit approvals).
 * A page under Pitch (navigation v2 d 4e): `section="under"`, back link "Pitch".
 */
const ReviewQueueScreenComponent: React.FC<{ accountId: number; campaignUuid: string }> = ({
  accountId,
  campaignUuid,
}) => {
  const intl = useIntl();
  const { isDemo } = useOutreachPlan();
  const { showError, showSuccess } = useShowToast();
  const invalidate = useInvalidateOutreach();
  const { campaign } = useCampaign(accountId, campaignUuid);
  const review = useReviewQueue(accountId, campaignUuid);
  const { addBlock } = useBlocks(accountId);
  const [decided, setDecided] = useState<ReadonlySet<string>>(new Set());
  const [picked, setPicked] = useState<string | null>(null);
  const [editingUuid, setEditingUuid] = useState<string | null>(null);

  const rows = useMemo(() => visibleReviewRows(review.queue?.messages ?? [], decided), [decided, review.queue]);
  const open = useMemo(() => rows.filter(isOpenPitch), [rows]);
  const current = useMemo(() => currentReviewRow(rows, picked), [picked, rows]);
  const hints = useMemo(
    () => (current === null ? [] : hintLines(intl, current.lintHints, current.quality.problems)),
    [current, intl]
  );
  const isBlocked = hints.some((hint) => hint.blocks);
  const approvedHere = useMemo(
    () => rows.filter((row) => decided.has(row.messageUuid) && row.status !== 'skipped').length,
    [decided, rows]
  );

  const failed = useCallback(
    (error: unknown) => {
      console.error(error);
      showError(intl.formatMessage(messages.failed));
    },
    [intl, showError]
  );
  const decide = useCallback(
    (uuid: string, run: (uuid: string) => Promise<unknown>) => {
      setPicked(nextOpenAfter(rows, uuid));
      setEditingUuid(null);
      run(uuid)
        .then(() => setDecided((set) => new Set(set).add(uuid)))
        .catch(failed);
    },
    [failed, rows]
  );
  const approve = useCallback(() => {
    if (current !== null && !isBlocked && !isDemo) {
      decide(current.messageUuid, review.approve);
    }
  }, [current, decide, isBlocked, isDemo, review.approve]);
  const skip = useCallback(() => {
    if (current !== null && !isDemo) {
      decide(current.messageUuid, review.skip);
    }
  }, [current, decide, isDemo, review.skip]);
  const edit = useCallback(() => {
    if (current !== null && !isDemo) {
      setEditingUuid(current.messageUuid);
    }
  }, [current, isDemo]);
  const keyActions = useMemo(() => ({ approve, edit, skip }), [approve, edit, skip]);
  useReviewKeys(current !== null && editingUuid === null, keyActions);

  const never = useCallback(() => {
    if (current === null) {
      return;
    }
    const name = current.targetName ?? current.contactName;
    addBlock({ kind: 'contact', workspaceContactUuid: current.workspaceContactUuid })
      .then(() => {
        invalidate(['review', 'campaigns']);
        showSuccess(intl.formatMessage(messages.neverDone, { name }));
      })
      .catch(failed);
  }, [addBlock, current, failed, intl, invalidate, showSuccess]);

  const skipAll = useCallback(() => {
    // eslint-disable-next-line no-alert -- a short confirm before many skips
    if (!window.confirm(intl.formatMessage(messages.skipAllConfirm, { count: open.length }))) {
      return;
    }
    const uuids = open.map((row) => row.messageUuid);
    uuids
      .reduce<Promise<unknown>>((chain, uuid) => chain.then(() => review.skip(uuid)), Promise.resolve())
      .then(() => setDecided((set) => new Set([...set, ...uuids])))
      .catch(failed);
  }, [failed, intl, open, review]);
  const approveAll = useCallback(() => {
    const uuids = open.map((row) => row.messageUuid);
    review
      .approveRest()
      .then(() => setDecided((set) => new Set([...set, ...uuids])))
      .catch(failed);
  }, [failed, open, review]);

  const content = () => {
    if (review.isLoading) {
      return <LoadingView />;
    }
    if (review.error !== null || review.queue === undefined) {
      return <ErrorView error={review.error ?? undefined} retry={review.retry} />;
    }
    if (current === null) {
      return (
        <ReviewAllDone accountId={accountId} campaignUuid={campaignUuid} campaign={campaign} approved={approvedHere} />
      );
    }
    const index = rows.findIndex((row) => row.messageUuid === current.messageUuid) + 1;
    return (
      <div className={styles.layout}>
        <ReviewQueueList
          rows={rows}
          currentUuid={current.messageUuid}
          onPick={setPicked}
          showAutopilotNote={campaign?.mode !== 'autopilot'}
        />
        <div className={styles.col}>
          <ReviewPitchCard
            key={current.messageUuid}
            accountId={accountId}
            campaignUuid={campaignUuid}
            message={current}
            hints={hints}
            isEditing={editingUuid === current.messageUuid}
            onEditDone={() => {
              setEditingUuid(null);
              setDecided((set) => new Set(set).add(current.messageUuid));
            }}
            onNever={never}
            disabled={isDemo}
          />
          {editingUuid !== current.messageUuid && (
            <ReviewStepBar
              index={index}
              total={rows.length}
              isBusy={review.busyUuid === current.messageUuid}
              isBlocked={isBlocked}
              disabled={isDemo}
              onSkip={skip}
              onEdit={edit}
              onApprove={approve}
            />
          )}
        </div>
      </div>
    );
  };

  const actions =
    open.length > 0 && !isDemo ? (
      <>
        <SMLButton size="sm" outline omitCap onClick={skipAll} testID="review-skip-all">
          {intl.formatMessage(messages.skipAll)}
        </SMLButton>
        {review.queue?.canApproveRest === true && (
          <SMLButton
            size="sm"
            color="primary"
            omitCap
            leftIcon={faCheck}
            onClick={approveAll}
            isLoading={review.isApprovingRest}
            testID="review-approve-all"
          >
            {intl.formatMessage(messages.approveAll, { count: open.length })}
          </SMLButton>
        )}
      </>
    ) : undefined;

  return (
    <OutreachShell
      section="under"
      title={
        open.length > 0
          ? intl.formatMessage(messages.title, { count: open.length })
          : intl.formatMessage(messages.titleNone)
      }
      subtitle={intl.formatMessage(messages.intro)}
      crumbs={[
        intl.formatMessage(outreachMessages.crumbPitch),
        campaign?.songTitle ?? '',
        intl.formatMessage(messages.crumb),
      ]}
      actions={actions}
    >
      {content()}
    </OutreachShell>
  );
};

export const ReviewQueueScreen = TypedMemo(ReviewQueueScreenComponent);
